import { useTrackBucketWithAb } from "@Data-Protocol/event-tracker"
import {
  Check,
  LucidePlus,
  LucideX,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react"
import { useAnswerFeedback } from "~/hooks/useAnswerFeedback"
import { cn } from "~/utils"

function Divider({ className }: { className?: string }) {
  return <div className={cn("h-3 w-px bg-white/15", className)} />
}

function MobileAnswerButtons({
  onAnswer,
}: {
  onAnswer: (v: "positive" | "negative") => void
}) {
  return (
    <>
      <button
        onClick={() => onAnswer("negative")}
        onTouchEnd={(e) => {
          e.preventDefault()
          onAnswer("negative")
        }}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:text-red-400 active:scale-95"
        aria-label="Thumbs down"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => onAnswer("positive")}
        onTouchEnd={(e) => {
          e.preventDefault()
          onAnswer("positive")
        }}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:text-green-400 active:scale-95"
        aria-label="Thumbs up"
      >
        <Check className="h-3.5 w-3.5" />
      </button>
    </>
  )
}

function DesktopAnswerButtons({
  onAnswer,
}: {
  onAnswer: (v: "positive" | "negative") => void
}) {
  return (
    <>
      <button
        onClick={() => onAnswer("positive")}
        className="text-white/50 transition-all hover:text-green-400 active:scale-95"
        aria-label="Thumbs up"
      >
        <ThumbsUp className="h-5 w-5" />
      </button>
      <button
        onClick={() => onAnswer("negative")}
        className="text-white/50 transition-all hover:text-red-400 active:scale-95"
        aria-label="Thumbs down"
      >
        <ThumbsDown className="h-5 w-5" />
      </button>
    </>
  )
}

export default function FeedbackQuestion({
  isControlBarVisible,
}: {
  isControlBarVisible: boolean
}) {
  const { abGroup, assignBucket } = useTrackBucketWithAb(2, {
    experiment: "social-proof-cancel-feedback-1",
  })
  const {
    showingFeedback,
    questionState,
    setQuestionState,
    answerState,
    setHovering,
    handleAnswer,
    questionLabel,
  } = useAnswerFeedback({ assignBucket, abGroup })

  const shrunk = questionState === "shrunk"
  const collapsed = questionState === "collapsed"
  const answered = questionState === "answered"

  if (!showingFeedback || questionState === "hidden") return null

  return (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={cn(
        "absolute left-2 z-20 sm:left-4 sm:top-4 2xl:top-20",
        isControlBarVisible ? "bottom-14" : "bottom-2"
      )}
    >
      {/* Mobile layout */}
      <div className="rounded-full bg-black/70 shadow-lg backdrop-blur-xl sm:hidden">
        <div className="flex items-center">
          <div
            className={cn(
              "transition-all duration-200 ease-out",
              shrunk
                ? "max-w-0 -translate-x-1 scale-x-0 opacity-0"
                : "max-w-48 translate-x-0 scale-x-100 opacity-100"
            )}
          >
            <div className="flex items-center gap-2 px-3.5 py-2">
              <div
                className={cn(
                  "origin-left overflow-hidden transition-all duration-200 ease-out",
                  shrunk
                    ? "-ml-2 max-w-0 -translate-x-1 scale-x-0 opacity-0"
                    : "max-w-24 translate-x-0 scale-x-100 opacity-100"
                )}
              >
                <p className="whitespace-nowrap text-xs font-medium text-white/80">
                  {questionLabel}
                </p>
              </div>
              {!shrunk && <Divider />}
              <MobileAnswerButtons onAnswer={(v) => handleAnswer(v, true)} />
            </div>
          </div>
          {shrunk && (
            <button
              onClick={() => setQuestionState("shown")}
              onTouchEnd={(e) => {
                e.preventDefault()
                setQuestionState("shown")
              }}
              className="flex items-center justify-center px-3 text-white/40 transition-all duration-200 ease-out hover:text-white/70"
              aria-label="Expand question"
            >
              <LucidePlus className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Desktop layout */}
      <div
        className={cn(
          "hidden items-center rounded-full bg-black/70 shadow-lg backdrop-blur-xl transition-all duration-200 ease-out sm:flex",
          collapsed ? "p-1" : "gap-2.5 px-3.5 py-2"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2.5 transition-all duration-200 ease-out",
            collapsed ? "h-0 w-0 overflow-hidden opacity-0" : "opacity-100"
          )}
        >
          {answered ? (
            <>
              <p className="text-sm font-medium text-white/80">
                {answerState === "positive"
                  ? "More like this coming your way"
                  : "Thanks for letting us know"}
              </p>
              <Divider
                className={cn(
                  shrunk ? "w-0 opacity-0" : "w-px opacity-100",
                  "transition-all duration-200 ease-out"
                )}
              />
            </>
          ) : (
            <>
              <div
                className={cn(
                  "origin-left overflow-hidden transition-all duration-200 ease-out",
                  shrunk
                    ? "-ml-2 max-w-0 -translate-x-1 scale-x-0 opacity-0"
                    : "max-w-48 translate-x-0 scale-x-100 opacity-100"
                )}
              >
                <p className="whitespace-nowrap text-sm font-medium text-white/80">
                  {questionLabel}
                </p>
              </div>
              {!shrunk && <Divider />}
              <DesktopAnswerButtons onAnswer={handleAnswer} />
              {abGroup === 2 && shrunk && <Divider />}
            </>
          )}
        </div>
        {abGroup === 2 && shrunk && (
          <button
            onClick={() => setQuestionState("hidden")}
            className={cn(
              "flex items-center justify-center transition-all duration-200 ease-out",
              collapsed
                ? "h-8 w-8 rounded-full text-white/40 hover:text-white/70"
                : "text-white/25 hover:text-white/60"
            )}
            aria-label="Dismiss question"
          >
            <LucideX className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  )
}
